import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Banner,
  Button,
  Text,
  Badge,
  InlineStack,
  Pagination,
  TextField,
  Select,
  ButtonGroup,
  BlockStack
} from '@shopify/polaris';
import { openprojectService } from '../services/openprojectService';
import { parseDurationToMinutes, calculateTimeDifferenceMinutes, getCommentText } from '../utils/timeFormat';
import { downloadCsv } from '../utils/csvExport';
import LoadingState from './shared/LoadingState';

const TelegramMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    userId: '',
    workPackageId: '',
    startDate: '',
    endDate: ''
  });
  const [users, setUsers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const fetchMessages = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await openprojectService.getTelegramMessages(page, pagination.limit, filters);
      setMessages(data.timeEntries || []);
      setPagination(prevPagination => data.pagination || prevPagination);
    } catch (err) {
      console.error('Error fetching telegram messages:', err);
      setError('Failed to fetch telegram messages');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadUsers();
  }, []); // Once on mount

  useEffect(() => {
    fetchMessages(1); // On mount, and whenever filters change
  }, [fetchMessages]);

  const loadUsers = async () => {
    try {
      // Load users from OpenProject API
      const usersList = await openprojectService.getUsers();
      
      // Also get users from telegram messages for additional filtering
      const messagesResponse = await openprojectService.getTelegramMessages(1, 1000, {});
      const messageUsers = [...new Set(messagesResponse.timeEntries?.map(entry => entry.userTitle).filter(Boolean) || [])];
      
      // Combine both sources and remove duplicates
      const allUsers = [...new Set([
        ...usersList.map(user => user.name || user.login),
        ...messageUsers
      ])].filter(Boolean);
      
      setUsers(allUsers.map(userTitle => ({
        label: userTitle,
        value: userTitle
      })));
    } catch (err) {
      console.error('Error loading users:', err);
      // Fallback: try to get users from messages only
      try {
        const messagesResponse = await openprojectService.getTelegramMessages(1, 1000, {});
        const uniqueUsers = [...new Set(messagesResponse.timeEntries?.map(entry => entry.userTitle).filter(Boolean) || [])];
        setUsers(uniqueUsers.map(userTitle => ({
          label: userTitle,
          value: userTitle
        })));
      } catch (fallbackErr) {
        console.error('Fallback user loading failed:', fallbackErr);
      }
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      workPackageId: '',
      startDate: '',
      endDate: ''
    });
  };

  const exportToExcel = () => {
    if (messages.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = [
      'Time Entry ID', 'Project', 'User', 'Task ID', 'Task Title',
      'Start Time', 'Stop Time', 'Time Diff (min)', 'Duration', 'Comment', 'Activity'
    ];

    const rows = messages.map((entry) => [
      entry.timeEntryId,
      getProjectTitle(entry.rawData),
      entry.userTitle,
      entry.workPackageId,
      entry.workPackageTitle,
      formatTime(entry.createdAt),
      formatTime(entry.updatedAt),
      calculateTimeDifferenceMinutes(entry.createdAt, entry.updatedAt),
      formatDuration(entry.hours),
      getCommentText(entry.comment),
      entry.activityTitle || 'N/A'
    ]);

    downloadCsv(`telegram_messages_${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDuration = (hours) => `${parseDurationToMinutes(hours)} min`;

  const getActivityTitle = (rawData) => {
    if (!rawData || !rawData._links || !rawData._links.activity) return 'N/A';
    return rawData._links.activity.title || 'N/A';
  };

  const getProjectTitle = (rawData) => {
    if (!rawData || !rawData._links || !rawData._links.project) return 'N/A';
    return rawData._links.project.title || 'N/A';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { tone: 'warning', children: 'Pending' },
      approved: { tone: 'success', children: 'Approved' },
      rejected: { tone: 'critical', children: 'Rejected' }
    };
    return statusMap[status] || { tone: 'warning', children: 'Pending' };
  };

  const handleStatusUpdate = async (timeEntryId, newStatus) => {
    try {
      await openprojectService.updateTimeEntryStatus(timeEntryId, newStatus);
      // Refresh the messages to show updated status
      await fetchMessages(pagination.page);
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };

  const handleResendTelegram = async (timeEntryId) => {
    try {
      await openprojectService.resendTelegramMessage(timeEntryId);
      // Show success message or refresh
      await fetchMessages(pagination.page);
    } catch (error) {
      console.error('Error resending Telegram message:', error);
      setError('Failed to resend Telegram message');
    }
  };


  const rows = messages.map((entry) => [
    entry.timeEntryId || 'N/A',
    getProjectTitle(entry.rawData),
    entry.workPackageId || 'N/A',
    entry.workPackageTitle || 'N/A',
    entry.userTitle || 'N/A',
    formatTime(entry.createdAt),
    formatTime(entry.updatedAt),
    calculateTimeDifferenceMinutes(entry.createdAt, entry.updatedAt) + ' min',
    formatDuration(entry.hours),
    getActivityTitle(entry.rawData),
    getCommentText(entry.comment),
    <Badge {...getStatusBadge(entry.status || 'pending')} />,
    <ButtonGroup>
      <Button 
        size="slim" 
        tone="success" 
        onClick={() => handleStatusUpdate(entry.timeEntryId, 'approved')}
        disabled={entry.status === 'approved'}
      >
        Approve
      </Button>
      <Button 
        size="slim" 
        tone="critical" 
        onClick={() => handleStatusUpdate(entry.timeEntryId, 'rejected')}
        disabled={entry.status === 'rejected'}
      >
        Reject
      </Button>
      <Button 
        size="slim" 
        tone="base" 
        onClick={() => handleResendTelegram(entry.timeEntryId)}
      >
        Resend
      </Button>
    </ButtonGroup>
  ]);

  const headings = [
    'Time Entry ID',
    'Project',
    'Task ID',
    'Task Title',
    'User',
    'Start Time',
    'Stop Time',
    'Time Diff',
    'Duration',
    'Activity',
    'Comment',
    'Status',
    'Actions'
  ];

  const handlePageChange = (page) => {
    fetchMessages(page);
  };

  const handleRefresh = () => {
    fetchMessages(pagination.page);
  };

  return (
    <Page
      title="Telegram Messages"
      subtitle={`Total messages: ${pagination.total}`}
      primaryAction={{
        content: 'Export to Excel',
        onAction: exportToExcel,
        disabled: messages.length === 0
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          onAction: handleRefresh,
          loading
        }
      ]}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <div style={{ padding: '1rem 0' }}>
              <BlockStack gap="400">
                <InlineStack gap="200" align="space-between">
                  <Text variant="headingMd" as="h3">Filters</Text>
                  <ButtonGroup>
                    <Button
                      onClick={() => setShowFilters(!showFilters)}
                      variant={showFilters ? 'primary' : 'tertiary'}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </Button>
                    <Button onClick={clearFilters} disabled={!Object.values(filters).some(v => v)}>
                      Clear Filters
                    </Button>
                  </ButtonGroup>
                </InlineStack>
                
                {showFilters && (
                  <BlockStack gap="300">
                    <InlineStack gap="300">
                      <div style={{ flex: 1 }}>
                        <Select
                          label="User"
                          options={[
                            { label: 'All Users', value: '' },
                            ...users
                          ]}
                          value={filters.userId}
                          onChange={(value) => handleFilterChange('userId', value)}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="Task ID"
                          value={filters.workPackageId}
                          onChange={(value) => handleFilterChange('workPackageId', value)}
                          placeholder="Enter task ID"
                        />
                      </div>
                    </InlineStack>
                    
                    <InlineStack gap="300">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="Start Date"
                          type="date"
                          value={filters.startDate}
                          onChange={(value) => handleFilterChange('startDate', value)}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TextField
                          label="End Date"
                          type="date"
                          value={filters.endDate}
                          onChange={(value) => handleFilterChange('endDate', value)}
                        />
                      </div>
                    </InlineStack>
                  </BlockStack>
                )}
              </BlockStack>
            </div>
          </Card>
        </Layout.Section>
        
        <Layout.Section>
          <Card>
            {loading && messages.length === 0 ? (
              <LoadingState label="Loading telegram messages" />
            ) : error ? (
              <Banner tone="critical" title="Error">
                <p>{error}</p>
                <Button onClick={handleRefresh}>Try Again</Button>
              </Banner>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 'var(--p-space-1600)' }}>
                <BlockStack gap="200" inlineAlign="center">
                  <Text variant="headingMd" as="h3" tone="subdued">
                    No telegram messages found
                  </Text>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Messages will appear here when time entries are processed
                  </Text>
                </BlockStack>
              </div>
            ) : (
              <>
                <div style={{ padding: '1rem 0' }}>
                  <InlineStack gap="200">
                    <Badge tone="info">
                      {messages.length} message{messages.length === 1 ? '' : 's'} on this page
                    </Badge>
                    <Text variant="bodyMd" tone="subdued">
                      Showing page {pagination.page} of {pagination.pages}
                    </Text>
                  </InlineStack>
                </div>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={headings}
                  rows={rows}
                  footerContent={`Showing ${messages.length} of ${pagination.total} messages`}
                />
                {pagination.pages > 1 && (
                  <div style={{ padding: '1rem 0', textAlign: 'center' }}>
                    <Pagination
                      hasPrevious={pagination.page > 1}
                      onPrevious={() => handlePageChange(pagination.page - 1)}
                      hasNext={pagination.page < pagination.pages}
                      onNext={() => handlePageChange(pagination.page + 1)}
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default TelegramMessages;
